import api from './api';

/** Registro amplio Varsis2 (tasas IVA y demás campos operativos). */
export interface Varsis2Dto {
  nint?: number;
  impuestotasa1?: number | null;
  impuestotasa2?: number | null;
  impuestotasa3?: number | null;
  impuestotasa4?: number | null;
  [key: string]: unknown;
}

/**
 * Fila para el ABM Variables del sistema.
 * En UI se muestra como Configuraciones; en API proviene de /VariablesSistema/campos (Varsis2).
 * `usuario` transporta el Nint de Varsis2 (string).
 */
export interface Varsis2ItemDto {
  usuario: string;
  seccion: string;
  campo: string;
  valor?: string | null;
  comentario?: string | null;
}

export interface Varsis2SearchParams {
  pageNumber?: number;
  pageSize?: number;
  searchTerm?: string;
  usuario?: string;
  seccion?: string;
  campo?: string;
}

export interface Varsis2PagedResult {
  items: Varsis2ItemDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

const DEFAULT_TASAS = [21, 10.5, 27, 0];

/** Mapea respuesta de /campos al DTO que usan las pantallas. */
const normalizeCampoToItem = (raw: Record<string, unknown>): Varsis2ItemDto => {
  const nint = raw.nint ?? raw.Nint;
  const tipo = raw.tipo ?? raw.Tipo;
  const etiqueta = raw.etiqueta ?? raw.Etiqueta;
  const comentarioFromApi = raw.comentario ?? raw.Comentario;
  return {
    usuario: nint != null && String(nint).trim() !== '' ? String(nint) : String(raw.usuario ?? raw.Usuario ?? ''),
    seccion: String(raw.seccion ?? raw.Seccion ?? ''),
    campo: String(raw.campo ?? raw.Campo ?? ''),
    valor: (raw.valor ?? raw.Valor ?? null) as string | null,
    comentario:
      comentarioFromApi != null
        ? String(comentarioFromApi)
        : etiqueta != null
          ? String(etiqueta)
          : tipo != null
            ? `Tipo: ${tipo}`
            : null,
  };
};

const parseNint = (usuario?: string): number | undefined => {
  const u = (usuario ?? '').trim();
  if (!u) return undefined;
  const n = Number(u);
  return Number.isFinite(n) ? n : undefined;
};

const variablesSistemaService = {
  getVariablesSistema: async (): Promise<Varsis2Dto[]> => {
    try {
      const resp = await api.get<Varsis2Dto[]>('/api/VariablesSistema');
      const raw = resp?.data;
      const items: Varsis2Dto[] = Array.isArray(raw)
        ? raw
        : Array.isArray((raw as { items?: Varsis2Dto[] })?.items)
          ? ((raw as { items: Varsis2Dto[] }).items)
          : [];
      return items;
    } catch (error) {
      console.error('Error al obtener variables del sistema:', error);
      return [];
    }
  },

  /** Listado paginado → GET /api/VariablesSistema/campos */
  getVarsis2Items: async (params: Varsis2SearchParams = {}): Promise<Varsis2PagedResult> => {
    const resp = await api.get('/api/VariablesSistema/campos', {
      params: {
        SearchTerm: params.searchTerm || undefined,
        Seccion: params.seccion || undefined,
        Campo: params.campo || undefined,
        Nint: parseNint(params.usuario),
        PageNumber: params.pageNumber ?? 1,
        PageSize: params.pageSize ?? 20,
      },
    });
    const data = resp.data as Record<string, unknown> | null;
    if (data && typeof data === 'object' && ('items' in data || 'Items' in data)) {
      const itemsRaw = (data.items ?? data.Items ?? []) as unknown[];
      return {
        items: itemsRaw.map((item) => normalizeCampoToItem(item as Record<string, unknown>)),
        totalCount: Number(data.totalCount ?? data.TotalCount ?? 0),
        pageNumber: Number(data.pageNumber ?? data.PageNumber ?? 1),
        pageSize: Number(data.pageSize ?? data.PageSize ?? 20),
        totalPages: Number(data.totalPages ?? data.TotalPages ?? 0),
      };
    }
    return {
      items: [],
      totalCount: 0,
      pageNumber: params.pageNumber ?? 1,
      pageSize: params.pageSize ?? 20,
      totalPages: 0,
    };
  },

  listAllCampos: async (
    params: Omit<Varsis2SearchParams, 'pageNumber' | 'pageSize'> = {},
  ): Promise<Varsis2ItemDto[]> => {
    const pageSize = 100;
    const acc: Varsis2ItemDto[] = [];
    let pageNumber = 1;
    let totalPages = 1;
    while (pageNumber <= totalPages && pageNumber <= 15) {
      const page = await variablesSistemaService.getVarsis2Items({
        ...params,
        pageNumber,
        pageSize,
      });
      acc.push(...page.items);
      totalPages = Math.max(1, page.totalPages || 1);
      if (page.items.length < pageSize) break;
      pageNumber += 1;
    }
    return acc;
  },

  getSecciones: async (): Promise<string[]> => {
    try {
      const resp = await api.get<string[]>('/api/VariablesSistema/secciones');
      const data = resp.data;
      if (!Array.isArray(data)) return [];
      return data.map((s) => String(s).trim()).filter(Boolean);
    } catch (error) {
      console.error('Error al obtener secciones de variables del sistema:', error);
      return [];
    }
  },

  /** Detalle → GET /api/VariablesSistema/campos/by-key */
  getByKey: async (
    seccion: string,
    campo: string,
    usuario = '',
  ): Promise<Varsis2ItemDto | null> => {
    try {
      const resp = await api.get('/api/VariablesSistema/campos/by-key', {
        params: {
          campo,
          nint: parseNint(usuario),
        },
      });
      if (!resp?.data) return null;
      const item = normalizeCampoToItem(resp.data as Record<string, unknown>);
      // El back filtra por campo; si enviaron sección, validar coherencia.
      if (seccion && item.seccion && seccion.trim().toLowerCase() !== item.seccion.trim().toLowerCase()) {
        return null;
      }
      return item;
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) return null;
      throw error;
    }
  },

  /** Update → PUT /api/VariablesSistema/campos (comentario no se persiste en Varsis2). */
  updateVarsis2: async (dto: Varsis2ItemDto): Promise<void> => {
    await api.put('/api/VariablesSistema/campos', {
      nint: parseNint(dto.usuario),
      campo: dto.campo,
      valor: dto.valor ?? null,
    });
  },

  /**
   * Código de ruta/zona por defecto al crear clientes y proveedores.
   * Lee el campo Varsis2 `CodigoRuta`. Si falta o es inválido, retorna 1.
   */
  getCodigoRuta: async (): Promise<number> => {
    try {
      const item = await variablesSistemaService.getByKey('', 'CodigoRuta');
      const n = Number(String(item?.valor ?? '').trim());
      if (Number.isFinite(n) && n >= 1) return n;
      return 1;
    } catch {
      return 1;
    }
  },

  getTasasIva: async (): Promise<number[]> => {
    const items = await variablesSistemaService.getVariablesSistema();
    const first = items[0];
    if (!first) return DEFAULT_TASAS;

    const t1 = first.impuestotasa1 != null ? Number(first.impuestotasa1) : DEFAULT_TASAS[0];
    const t2 = first.impuestotasa2 != null ? Number(first.impuestotasa2) : DEFAULT_TASAS[1];
    const t3 = first.impuestotasa3 != null ? Number(first.impuestotasa3) : DEFAULT_TASAS[2];
    const t4 = first.impuestotasa4 != null ? Number(first.impuestotasa4) : DEFAULT_TASAS[3];

    return [t1, t2, t3, t4];
  },

  getImpuestoTasas: async (): Promise<{
    impuestotasa1?: number;
    impuestotasa2?: number;
    impuestotasa3?: number;
    impuestotasa4?: number;
  }> => {
    const arr = await variablesSistemaService.getTasasIva();
    return {
      impuestotasa1: arr[0],
      impuestotasa2: arr[1],
      impuestotasa3: arr[2],
      impuestotasa4: arr[3],
    };
  },
};

export default variablesSistemaService;
